import { useCallback } from 'react';
import { useGetUserPromptsQuery, promptApi } from '../../../store/services';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import { selectUserFeedPrompts, selectUserFeedCache } from '../../../store/slices/prompts';
import { PromptDto } from '../../../components/types';

type UseUserPromptsFeedReturn = {
    prompts: PromptDto[];
    loading: boolean;
    hasMore: boolean;
    loadMore: () => Promise<void>;
    isSuccess: boolean;
};

/**
 * Хук для управления лентой промптов публичного профиля пользователя с автоподгрузкой
 * @param publicId - publicId пользователя
 * @param pageSize - Количество промптов на странице (по умолчанию 20)
 * @returns Объект с данными и методами управления
 */
export const useUserPromptsFeed = (publicId: string, pageSize: number = 20): UseUserPromptsFeedReturn => {
    const dispatch = useAppDispatch();

    // Получаем данные из Redux
    const prompts = useAppSelector(state => selectUserFeedPrompts(state, publicId));
    const cache = useAppSelector(state => selectUserFeedCache(state, publicId));

    // RTK Query только для выполнения запросов
    const { isFetching, isSuccess } = useGetUserPromptsQuery(
        {
            publicId,
            limit: pageSize,
        },
        { skip: !publicId },
    );

    // Функция для загрузки следующей страницы
    const loadMore = useCallback(async (): Promise<void> => {
        if (!cache?.nextCursor || !cache?.hasMore || isFetching) {
            return;
        }

        try {
            await dispatch(
                promptApi.endpoints.getUserPrompts.initiate(
                    { publicId, cursor: cache.nextCursor, limit: pageSize },
                    { subscribe: false, forceRefetch: true },
                ),
            ).unwrap();
        } catch (error) {
            console.error('Failed to load more user prompts:', error);
        }
    }, [cache?.nextCursor, cache?.hasMore, isFetching, publicId, pageSize, dispatch]);

    return {
        prompts,
        loading: isFetching,
        hasMore: cache?.hasMore ?? true,
        loadMore,
        isSuccess,
    };
};
